// One prospect: facts with evidence, contacts (every address with its source), drafts (edit,
// approve, copy, record as sent by hand), and the interaction history entered by hand.
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Badge, Empty, ErrorMsg, Loading, PageHead, Section, Time, Unknown } from '../../components/ui';
import { api, errorText, newIdempotencyKey } from '../../lib/api';
import { countryName } from '../../lib/format';
import { INTERACTION_KIND, PROSPECT_STATUS, STATUS_TEXT, type Draft, type ProspectDetail } from '../../lib/prospectTypes';
import { useApi, useSession } from '../../lib/session';

type Act = (fn: () => Promise<unknown>, done?: string) => Promise<boolean>;

function Header({ p, act }: { p: ProspectDetail; act: Act }) {
  const [next, setNext] = useState(p.next_action ?? '');
  const [due, setDue] = useState(p.next_action_due ?? '');
  useEffect(() => {
    setNext(p.next_action ?? '');
    setDue(p.next_action_due ?? '');
  }, [p.next_action, p.next_action_due]);
  const patch = (patch: Record<string, unknown>, done: string) =>
    act(() => api(`/api/prospects/${p.id}`, { method: 'PATCH', body: { version: p.version, patch } }), done);
  return (
    <Section title="Relationship">
      <div className="form-grid">
        <label className="field">
          <span>Status</span>
          <select value={p.status} onChange={(e) => void patch({ status: e.target.value }, 'Status saved.')}>
            {PROSPECT_STATUS.map((s) => (
              <option key={s} value={s}>
                {STATUS_TEXT[s]}
              </option>
            ))}
          </select>
        </label>
        <div className="field">
          <span>Website</span>
          <p className="small">
            {p.website_url ? (
              <a href={p.website_url} target="_blank" rel="noreferrer noopener">
                {p.website_url}
              </a>
            ) : (
              <Unknown>none</Unknown>
            )}
          </p>
        </div>
        <label className="field">
          <span>Next action</span>
          <input value={next} onChange={(e) => setNext(e.target.value)} />
        </label>
        <label className="field">
          <span>Due</span>
          <input type="date" value={due} onChange={(e) => setDue(e.target.value)} />
        </label>
        <div className="span2">
          <button
            type="button"
            className="btn small"
            disabled={next === (p.next_action ?? '') && due === (p.next_action_due ?? '')}
            onClick={() => void patch({ next_action: next || null, next_action_due: due || null }, 'Follow-up saved.')}
          >
            Save follow-up
          </button>
        </div>
      </div>
      {p.blocked && <p className="msg warn small">Do not contact: {p.blocked}</p>}
      {p.competitor && (
        <p className="msg warn small">
          Also in the competitor directory: <Link to={`/competitors/${p.competitor.id}`}>{p.competitor.name}</Link>. It was added here on purpose.
        </p>
      )}
    </Section>
  );
}

function Facts({ p, act }: { p: ProspectDetail; act: Act }) {
  const [field, setField] = useState('project');
  const [value, setValue] = useState('');
  const [src, setSrc] = useState('');
  const facts = p.facts.filter((f) => f.field !== 'email');
  return (
    <Section
      title="Facts"
      aside={
        p.website_url && (
          <button type="button" className="btn small" onClick={() => void act(() => api(`/api/prospects/${p.id}/research`, { method: 'POST', idempotencyKey: newIdempotencyKey() }), 'Website research queued; reload in a minute.')}>
            Read their website
          </button>
        )
      }
    >
      <p className="small muted">Drafts use verified facts only. Facts the website research found are proposals until you verify them.</p>
      {facts.length === 0 ? (
        <Empty>No fact yet.</Empty>
      ) : (
        <ul className="facts">
          {facts.map((f) => (
            <li key={f.id} className="fact">
              <div className="fact-head">
                <span className="fact-value">{f.value}</span>
                <span className="muted small">
                  {' '}
                  · {f.field} · {f.state} · {f.method}
                </span>
              </div>
              {f.excerpt && <blockquote className="excerpt">{f.excerpt}</blockquote>}
              {f.source_url && (
                <p className="small muted">
                  Source:{' '}
                  <a href={f.source_url} target="_blank" rel="noreferrer noopener">
                    {f.source_url}
                  </a>
                </p>
              )}
              {f.state === 'proposed' && (
                <div className="row gap">
                  <button type="button" className="btn small" onClick={() => void act(() => api(`/api/prospect-facts/${f.id}/review`, { body: { state: 'verified' } }), 'Fact verified.')}>
                    Verify
                  </button>
                  <button type="button" className="btn small" onClick={() => void act(() => api(`/api/prospect-facts/${f.id}/review`, { body: { state: 'rejected' } }), 'Fact rejected.')}>
                    Reject
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      <form
        className="form-grid"
        aria-label="Add a fact"
        onSubmit={(e) => {
          e.preventDefault();
          void act(() => api(`/api/prospects/${p.id}/facts`, { body: { field, value, source_url: src || null } }), 'Fact added.').then((ok) => {
            if (ok) {
              setValue('');
              setSrc('');
            }
          });
        }}
      >
        <label className="field">
          <span>Kind of fact</span>
          <select value={field} onChange={(e) => setField(e.target.value)}>
            <option value="project">their project</option>
            <option value="fit">why they fit</option>
            <option value="description">description</option>
            <option value="other">other</option>
          </select>
        </label>
        <label className="field">
          <span>Source URL (optional)</span>
          <input value={src} onChange={(e) => setSrc(e.target.value)} />
        </label>
        <label className="field span2">
          <span>Fact (you checked it)</span>
          <input value={value} onChange={(e) => setValue(e.target.value)} />
        </label>
        <div className="span2">
          <button type="submit" className="btn small" disabled={!value.trim()}>
            Add fact
          </button>
        </div>
      </form>
    </Section>
  );
}

function Contacts({ p, act }: { p: ProspectDetail; act: Act }) {
  const [c, setC] = useState({ name: '', role: '', email: '', email_source: '', phone: '' });
  const found = p.facts.filter((f) => f.field === 'email' && f.state !== 'rejected' && !p.contacts.some((x) => x.email === f.value));
  return (
    <Section title="Contacts">
      {p.contacts.length === 0 ? (
        <Empty>No contact yet.</Empty>
      ) : (
        <ul className="plain">
          {p.contacts.map((x) => (
            <li key={x.id} className="stack-s">
              <strong>{x.name ?? 'unnamed'}</strong> {x.role && <span className="muted small">· {x.role}</span>}{' '}
              {x.email && (
                <span className="small">
                  · {x.email} <span className="muted">(source: {x.email_source})</span>
                </span>
              )}
              {x.phone && <span className="small"> · {x.phone}</span>}
              {x.last_emailed_at && (
                <span className="small muted">
                  {' '}
                  · last emailed <Time iso={x.last_emailed_at} />
                </span>
              )}
              {x.blocked ? (
                <Badge tone="bad">{x.blocked}</Badge>
              ) : (
                <button type="button" className="btn small" onClick={() => void act(() => api(`/api/prospect-contacts/${x.id}/opt-out`, { body: { reason: 'opted out' } }), 'Opted out; the address is on the do-not-contact list.')}>
                  Mark as opted out
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {found.length > 0 && (
        <>
          <h3 className="sub">Addresses found on their website</h3>
          <ul className="plain small">
            {found.map((f) => (
              <li key={f.id}>
                {f.value} · on{' '}
                <a href={f.source_url ?? '#'} target="_blank" rel="noreferrer noopener">
                  {f.source_url}
                </a>{' '}
                <button type="button" className="btn small" onClick={() => void act(() => api(`/api/prospects/${p.id}/contacts`, { body: { from_fact_id: f.id } }), 'Contact added from the website.')}>
                  Use as contact
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
      <form
        className="form-grid"
        aria-label="Add a contact"
        onSubmit={(e) => {
          e.preventDefault();
          const body = Object.fromEntries(Object.entries(c).map(([k, v]) => [k, v.trim() || null]));
          void act(() => api(`/api/prospects/${p.id}/contacts`, { body }), 'Contact added.').then((ok) => ok && setC({ name: '', role: '', email: '', email_source: '', phone: '' }));
        }}
      >
        <label className="field">
          <span>Name</span>
          <input value={c.name} onChange={(e) => setC({ ...c, name: e.target.value })} />
        </label>
        <label className="field">
          <span>Role</span>
          <input value={c.role} onChange={(e) => setC({ ...c, role: e.target.value })} />
        </label>
        <label className="field">
          <span>Email</span>
          <input type="email" value={c.email} onChange={(e) => setC({ ...c, email: e.target.value })} />
        </label>
        <label className="field">
          <span>Where the address comes from</span>
          <input value={c.email_source} placeholder="URL, or “given by …”" onChange={(e) => setC({ ...c, email_source: e.target.value })} />
        </label>
        <label className="field">
          <span>Phone</span>
          <input value={c.phone} onChange={(e) => setC({ ...c, phone: e.target.value })} />
        </label>
        <div className="span2">
          <button type="submit" className="btn small">
            Add contact
          </button>
          <span className="muted small"> An address is never guessed: say where it comes from.</span>
        </div>
      </form>
    </Section>
  );
}

function DraftCard({ d, p, act, owner }: { d: Draft; p: ProspectDetail; act: Act; owner: boolean }) {
  const [subject, setSubject] = useState(d.subject);
  const [body, setBody] = useState(d.body);
  const [repeat, setRepeat] = useState(false);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    setSubject(d.subject);
    setBody(d.body);
  }, [d.subject, d.body]);
  const dirty = subject !== d.subject || body !== d.body;
  const editable = d.status === 'draft' || d.status === 'approved';
  const contact = p.contacts.find((c) => c.id === d.contact_id);
  async function copy() {
    try {
      await navigator.clipboard.writeText(`${subject}\n\n${body}`);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }
  return (
    <li className="run">
      <div className="row gap center wrap">
        <Badge tone={d.status === 'approved' ? 'ok' : undefined}>{d.status.replace('_', ' ')}</Badge>
        <span className="small muted">
          {d.generated_by}
          {d.model?.model ? ` (${d.model.provider} ${d.model.model}, ${d.model.is_local ? 'local' : 'cloud'})` : ''} · {d.language} · to{' '}
          {contact ? `${contact.name ?? ''} ${contact.email ?? ''}` : 'no contact chosen'} · <Time iso={d.updated_at} />
        </span>
      </div>
      {d.warnings.map((w) => (
        <p key={w} className="msg warn small">
          {w}
        </p>
      ))}
      {editable ? (
        <div className="stack">
          <label className="field">
            <span>Subject</span>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} />
          </label>
          <label className="field">
            <span>Body</span>
            <textarea rows={10} value={body} onChange={(e) => setBody(e.target.value)} />
          </label>
        </div>
      ) : (
        <pre className="snapshot-text">{`${d.subject}\n\n${d.body}`}</pre>
      )}
      {d.placeholders.length > 0 && <p className="small err-text">Replace before approval: {d.placeholders.join(', ')}</p>}
      <div className="row gap wrap">
        {editable && (
          <button type="button" className="btn small" disabled={!dirty} onClick={() => void act(() => api(`/api/drafts/${d.id}`, { method: 'PATCH', body: { subject, body, version: d.version } }), d.status === 'approved' ? 'Saved; the approval was revoked.' : 'Draft saved.')}>
            Save
          </button>
        )}
        {owner && d.status === 'draft' && (
          <button type="button" className="btn small primary" disabled={dirty} onClick={() => void act(() => api(`/api/drafts/${d.id}/approve`, { body: { version: d.version } }), 'Draft approved.')}>
            Approve
          </button>
        )}
        <button type="button" className="btn small" onClick={() => void copy()}>
          {copied ? 'Copied' : 'Copy text'}
        </button>
        {d.status === 'approved' && (
          <>
            <label className="check small">
              <input type="checkbox" checked={repeat} onChange={(e) => setRepeat(e.target.checked)} /> I know this contact was emailed in the last {p.repeat_days} days
            </label>
            <button
              type="button"
              className="btn small primary"
              onClick={() => void act(() => api(`/api/drafts/${d.id}/sent`, { body: { confirm_repeat: repeat }, idempotencyKey: newIdempotencyKey() }), 'Recorded as sent.')}
            >
              I sent it from my mailbox
            </button>
          </>
        )}
        {d.status !== 'sent_manually' && d.status !== 'discarded' && (
          <button type="button" className="btn small ghost" onClick={() => void act(() => api(`/api/drafts/${d.id}/discard`, { method: 'POST' }), 'Draft discarded.')}>
            Discard
          </button>
        )}
      </div>
    </li>
  );
}

function Drafts({ p, act, owner }: { p: ProspectDetail; act: Act; owner: boolean }) {
  const [lang, setLang] = useState('en');
  const [contact, setContact] = useState('');
  const [projects, setProjects] = useState<string[]>([]);
  const make = (mode: string) =>
    act(
      () => api(`/api/prospects/${p.id}/drafts`, { body: { mode, language: lang, contact_id: contact || null, project_ids: projects }, idempotencyKey: newIdempotencyKey() }),
      mode === 'model' ? 'Queued for the model agent; the draft appears here when it is done.' : 'Draft created.',
    );
  const live = p.drafts.filter((d) => d.status !== 'discarded');
  return (
    <Section title="Outreach drafts">
      <p className="small muted">
        Built from verified facts and Mirage projects approved for public use (public description only). The app never sends: copy the approved text into
        your mailbox, then record it here.
      </p>
      <div className="form-grid">
        <label className="field">
          <span>Language</span>
          <select value={lang} onChange={(e) => setLang(e.target.value)}>
            <option value="en">English</option>
            <option value="fr">French</option>
          </select>
        </label>
        <label className="field">
          <span>To</span>
          <select value={contact} onChange={(e) => setContact(e.target.value)}>
            <option value="">no contact yet</option>
            {p.contacts
              .filter((c) => !c.blocked)
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name ?? c.email}
                </option>
              ))}
          </select>
        </label>
        <fieldset className="field span2">
          <legend>Mirage projects to mention ({p.materials.length} approved for public use)</legend>
          {p.materials.length === 0 && <span className="muted small">None: mark a Content Studio project as allowed for publication with a public description.</span>}
          {p.materials.map((m) => (
            <label key={m.id} className="check block">
              <input type="checkbox" checked={projects.includes(m.id)} onChange={(e) => setProjects(e.target.checked ? [...projects, m.id] : projects.filter((x) => x !== m.id))} /> {m.title}
              {m.location && ` (${m.location})`}
            </label>
          ))}
        </fieldset>
        <div className="span2 row gap wrap">
          <button type="button" className="btn small" disabled={!!p.blocked} onClick={() => void make('template')}>
            Draft from the template
          </button>
          <button type="button" className="btn small" disabled={!!p.blocked || !p.model_drafts.enabled} title={p.model_drafts.reason ?? ''} onClick={() => void make('model')}>
            Draft with the model
          </button>
        </div>
      </div>
      {!p.model_drafts.enabled && <p className="small muted">{p.model_drafts.reason}</p>}
      {live.length === 0 ? (
        <Empty>No draft yet.</Empty>
      ) : (
        <ul className="runs">
          {live.map((d) => (
            <DraftCard key={d.id} d={d} p={p} act={act} owner={owner} />
          ))}
        </ul>
      )}
    </Section>
  );
}

function History({ p, act }: { p: ProspectDetail; act: Act }) {
  const [kind, setKind] = useState('note');
  const [summary, setSummary] = useState('');
  const [when, setWhen] = useState('');
  return (
    <Section title="History (entered by hand)">
      <form
        className="form-grid"
        aria-label="Record an interaction"
        onSubmit={(e) => {
          e.preventDefault();
          void act(
            () => api(`/api/prospects/${p.id}/interactions`, { body: { kind, summary, occurred_at: when ? new Date(when).toISOString() : null }, idempotencyKey: newIdempotencyKey() }),
            'Recorded.',
          ).then((ok) => ok && setSummary(''));
        }}
      >
        <label className="field">
          <span>What happened</span>
          <select value={kind} onChange={(e) => setKind(e.target.value)}>
            {INTERACTION_KIND.map((k) => (
              <option key={k} value={k}>
                {k.replace('_', ' ')}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>When (empty = now)</span>
          <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} />
        </label>
        <label className="field span2">
          <span>Summary</span>
          <input value={summary} onChange={(e) => setSummary(e.target.value)} />
        </label>
        <div className="span2">
          <button type="submit" className="btn small" disabled={!summary.trim()}>
            Record
          </button>
          <span className="muted small"> Emails you sent are recorded from their draft, so repeats are caught.</span>
        </div>
      </form>
      {p.interactions.length === 0 ? (
        <Empty>Nothing recorded yet.</Empty>
      ) : (
        <ul className="plain small">
          {p.interactions.map((i) => (
            <li key={i.id}>
              <Time iso={i.occurred_at} /> · <strong>{i.kind.replace('_', ' ')}</strong> · {i.summary}
              {i.outcome && <span className="muted"> · {i.outcome}</span>}
            </li>
          ))}
        </ul>
      )}
      <p className="muted small">Enquiries and meetings are counted in the weekly report as recorded here; no attribution is made automatically.</p>
    </Section>
  );
}

export function ProspectPage() {
  const { id } = useParams();
  const { user } = useSession();
  const nav = useNavigate();
  const res = useApi<ProspectDetail>(`/api/prospects/${id}`);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const act: Act = async (fn, done) => {
    setErr('');
    setMsg('');
    try {
      await fn();
      if (done) setMsg(done);
      res.reload();
      return true;
    } catch (e) {
      setErr(errorText(e));
      return false;
    }
  };
  if (res.loading && !res.data) return <Loading what="Loading the prospect" />;
  if (!res.data) return <ErrorMsg>{res.error}</ErrorMsg>;
  const p = res.data;
  return (
    <div className="page">
      <p className="crumbs small">
        <Link to="/prospects">Prospects</Link> / {p.name}
      </p>
      <PageHead title={p.name} meta={`${p.kind} · ${[p.city, p.country && countryName(p.country)].filter(Boolean).join(', ') || 'location unknown'} · ${STATUS_TEXT[p.status]}`} />
      {msg && <p className="msg ok small">{msg}</p>}
      <ErrorMsg>{err}</ErrorMsg>
      <div className="two-col">
        <div>
          <Header p={p} act={act} />
          <Facts p={p} act={act} />
          <Contacts p={p} act={act} />
        </div>
        <div>
          <Drafts p={p} act={act} owner={user?.role === 'owner'} />
          <History p={p} act={act} />
        </div>
      </div>
      {user?.role === 'owner' && (
        <p className="small">
          <button
            type="button"
            className="btn small ghost"
            onClick={() => {
              if (!window.confirm(`Delete ${p.name} with its contacts, drafts and history? This cannot be undone.`)) return;
              void act(() => api(`/api/prospects/${p.id}`, { method: 'DELETE' })).then((ok) => ok && nav('/prospects'));
            }}
          >
            Delete this prospect and its personal data
          </button>
        </p>
      )}
      {p.jobs.length > 0 && (
        <p className="small muted">
          Jobs:{' '}
          {p.jobs.map((j) => (
            <span key={j.id}>
              <Link to={`/jobs/${j.id}`}>{j.type.replace('_', ' ')}</Link> {j.status}{' '}
            </span>
          ))}
        </p>
      )}
    </div>
  );
}

